// biome-ignore lint/style/noRestrictedImports: component usage
import { Indicator, Root } from "@radix-ui/react-checkbox";
import { type VariantProps, cv } from "@suse-o11y/class-variance";
import { IconCheck } from "@suse-o11y/icons";
import { type IgnorePasswordManagers, ignorePasswordManagerAttributes } from "@suse-o11y/ignore-password-managers";
import { mergeRefs } from "@suse-o11y/merge-refs";
import { useComputed } from "@suse-o11y/signal";
import { useOnInvalidFormFocus } from "@suse-o11y/signal/form";
import React from "react";

import { EventGate } from "~/components/event-gate";
import { useFormContext } from "~/components/form/use-form-context";
import { useFormFieldContext } from "~/components/form/use-form-field-context";

import styles from "./_checkbox.module.css";

/**
 * Check out Figma for the design of the checkbox:
 *
 * @link https://www.figma.com/design/YIkZms7ygvgqXYnbzDFr8Y/%23-StackState-DS?node-id=4390-1600&t=kd8FoSVxTPTC1RmE-4
 */
const variants = cv(styles.checkbox, {
	variants: {
		size: {
			sm: styles.sm,
			md: styles.md,
			lg: styles.lg,
		},
	},

	defaultVariants: {
		size: "md",
	},
});

export interface CheckboxProps
	extends IgnorePasswordManagers,
		VariantProps<typeof variants>,
		Omit<React.ComponentProps<typeof Root>, "onMouseDown" | "onMouseUp" | "onCheckedChange" | "children"> {
	loading?: boolean;
	onCheckedChange?(checked: boolean): void;
}

export const Checkbox = React.forwardRef<React.ElementRef<typeof Root>, CheckboxProps>(
	(
		{
			allowPasswordManager,
			className,
			size,
			disabled,
			loading,
			checked,
			defaultChecked,
			onBlur,
			onCheckedChange,
			...props
		},
		ref,
	) => {
		const form = useFormContext();
		const formField = useFormFieldContext();
		const focusRef = useOnInvalidFormFocus(formField?.state);
		const submitting = useComputed((monitor) => formField?.state.isSubmitting(monitor), [formField?.state]);

		return (
			<EventGate<typeof Root>
				aria-busy={submitting || loading}
				aria-disabled={disabled}
				checked={checked}
				className={variants({ size, className })}
				defaultChecked={defaultChecked}
				form={form?.id}
				id={formField?.id}
				listening={!(disabled || loading || submitting)}
				onBlur={(event) => {
					formField?.state.setTouched(true);
					onBlur?.(event);
				}}
				// prevent focus-within from clicking
				onCheckedChange={(checkedState) => {
					// true -> true
					// indeterminate -> true
					// false -> false
					onCheckedChange?.(checkedState !== false);
				}}
				onMouseDown={(event) => event.preventDefault()}
				onMouseUp={(event) => event.preventDefault()}
				ref={mergeRefs([ref, focusRef])}
				{...ignorePasswordManagerAttributes(allowPasswordManager)}
				{...props}
			>
				<Root>
					<Indicator className="text-[0.5em]">
						{checked === "indeterminate" ? (
							<span className="block size-[1em] rounded-xs bg-current" />
						) : (
							<IconCheck className="text-white" />
						)}
					</Indicator>
				</Root>
			</EventGate>
		);
	},
);

if (import.meta.env.DEV) {
	Checkbox.displayName = "Checkbox";
}
